import { useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useBranches } from '../../hooks/useBranches';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { usePermission } from '../../hooks/usePermission';
import { date, fine, money, today } from '../../utils/format';
import { Empty, ErrorNotice, Loading, PageHeader, Stat, Table } from '../../components/ui';
import { DateInput, Field, Select, TextInput } from '../../components/ui/Form';

interface SessionRow {
  id: string;
  doc_no: string;
  session_date: string;
  status: string;
  branch_name: string;
  expected_items: number;
  counted_items: number;
  variance_items: number;
  variance_fine_wt: string;
}

interface Variance {
  expectedItems: number;
  countedItems: number;
  missing: { itemCode: string; fineWt: string; cost: string }[];
  unexpected: string[];
  weightDiffers: { itemCode: string; expected: string; counted: string }[];
  varianceFineWt: string;
  varianceValue: string;
}

interface Scanned {
  code: string;
  outcome: string;
  itemCode: string | null;
}

const OUTCOME_TONE: Record<string, string> = {
  PRESENT: 'text-success',
  WEIGHT_DIFFERS: 'text-gold-700',
  UNEXPECTED: 'text-danger',
};

/**
 * Counting the shelf against the books.
 *
 * Opening a session freezes what the books expect into an immutable snapshot,
 * so counting can run while the branch keeps trading. Closing raises an
 * adjustment for what was not found — it does not post one, because writing
 * gold off needs a second person.
 */
export function AuditPage() {
  const { can } = usePermission();
  const client = useQueryClient();
  const onEnter = useKeyboardForm();
  const { branches, defaultBranchId } = useBranches();
  const scanRef = useRef<HTMLInputElement>(null);

  const [active, setActive] = useState<string | null>(null);
  const [barcode, setBarcode] = useState('');
  const [scanned, setScanned] = useState<Scanned[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [notes, setNotes] = useState('');
  const [form, setForm] = useState({ branchId: '', sessionDate: today() });
  const [closed, setClosed] = useState<{ adjustmentId: string | null; variance: Variance } | null>(null);

  const list = useQuery({ queryKey: ['audits'], queryFn: () => api.get<{ data: SessionRow[] }>('/audits') });
  const variance = useQuery({
    queryKey: ['auditVariance', active],
    queryFn: () => api.get<Variance>(`/audits/${active}/variance`),
    enabled: active !== null,
  });

  const open = useMutation({
    mutationFn: () =>
      api.post<{ sessionId: string; docNo: string; expectedItems: number; expectedFineWt: string }>('/audits', {
        branchId: form.branchId || defaultBranchId,
        sessionDate: form.sessionDate,
      }),
    onSuccess: (r) => {
      setActive(r.sessionId);
      setScanned([]);
      setClosed(null);
      setError(null);
      void client.invalidateQueries({ queryKey: ['audits'] });
      setTimeout(() => scanRef.current?.focus(), 50);
    },
    onError: setError,
  });

  const count = useMutation({
    mutationFn: (code: string) =>
      api.post<{ outcome: string; itemCode: string | null }>(`/audits/${active}/count`, { scannedCode: code }),
    onSuccess: (r, code) => {
      setScanned((c) => [{ code, outcome: r.outcome, itemCode: r.itemCode }, ...c]);
      setBarcode('');
      setError(null);
      void client.invalidateQueries({ queryKey: ['auditVariance'] });
      scanRef.current?.focus();
    },
    onError: (err) => {
      setError(err);
      scanRef.current?.focus();
    },
  });

  const close = useMutation({
    mutationFn: () =>
      api.post<{ adjustmentId: string | null; variance: Variance }>(`/audits/${active}/close`, { notes }),
    onSuccess: (r) => {
      setClosed(r);
      setActive(null);
      setNotes('');
      void client.invalidateQueries({ queryKey: ['audits'] });
      void client.invalidateQueries({ queryKey: ['adjustments'] });
    },
    onError: setError,
  });

  const scan = (e: FormEvent): void => {
    e.preventDefault();
    const code = barcode.trim();
    if (code) count.mutate(code);
  };

  const rows = list.data?.data ?? [];

  return (
    <>
      <PageHeader
        title="Physical audit"
        subtitle="What the books expect is frozen when the session opens, so the shop can keep trading while the shelf is counted."
      />
      <ErrorNotice error={error} />

      {closed ? (
        <div className="mb-4 rounded border border-gold-400 bg-gold-50 p-4 text-sm">
          <p className="font-medium">
            Counted {closed.variance.countedItems} of {closed.variance.expectedItems} pieces.
          </p>
          {closed.variance.missing.length > 0 ? (
            <>
              <p className="mt-1">
                {closed.variance.missing.length} not found —{' '}
                <span className="num">{fine(closed.variance.varianceFineWt)}</span>, worth{' '}
                <span className="num">{money(closed.variance.varianceValue)}</span>.
              </p>
              <p className="mt-1 text-xs text-ink-soft">
                An adjustment has been raised. The pieces stay on the books until somebody else approves writing them
                off — see Adjustments.
              </p>
            </>
          ) : (
            <p className="mt-1 text-success">Everything the books expected was on the shelf.</p>
          )}
          <button className="btn-ghost mt-3 py-1" onClick={() => setClosed(null)}>
            Done
          </button>
        </div>
      ) : null}

      {active === null ? (
        <>
          {can('stock.audit_conduct') ? (
            <section className="card mb-5">
              <h2 className="text-sm font-semibold">Start a new count</h2>
              <p className="mb-3 text-2xs text-ink-soft">
                Choose the branch and the day. Scanning starts as soon as the session opens.
              </p>
              <div
                className={`grid items-end gap-3 ${
                  branches.length > 1 ? 'sm:grid-cols-[minmax(0,1fr)_12rem_auto]' : 'sm:grid-cols-[12rem_auto]'
                }`}
              >
                {branches.length > 1 ? (
                  <Field label="Branch">
                    <Select
                      value={form.branchId || (defaultBranchId ?? '')}
                      onChange={(v) => setForm({ ...form, branchId: v })}
                      options={branches.map((b) => ({ value: b.id, label: b.name }))}
                    />
                  </Field>
                ) : null}
                <Field label="Date">
                  <DateInput value={form.sessionDate} onChange={(v) => setForm({ ...form, sessionDate: v })} />
                </Field>
                <button
                  className="btn-primary h-9 justify-self-start"
                  onClick={() => open.mutate()}
                  disabled={open.isPending}
                >
                  {open.isPending ? 'Opening…' : 'Start a count'}
                </button>
              </div>
            </section>
          ) : null}

          <h2 className="mb-2 text-sm font-semibold">Past counts</h2>

          {list.isLoading ? <Loading /> : null}
          {!list.isLoading && rows.length === 0 ? <Empty message="No counts yet." /> : null}

          {rows.length > 0 ? (
            <Table
              head={['Date', 'Session', 'Branch', 'Expected', 'Counted', 'Not found', 'Fine difference', 'Status']}
            >
              {rows.map((s) => (
                <tr key={s.id}>
                  <td className="td">{date(s.session_date)}</td>
                  <td className="td num">{s.doc_no}</td>
                  <td className="td">{s.branch_name}</td>
                  <td className="td num text-right">{s.expected_items}</td>
                  <td className="td num text-right">{s.counted_items}</td>
                  <td className="td num text-right">
                    {s.variance_items > 0 ? (
                      <span className="text-danger">{s.variance_items}</span>
                    ) : (
                      <span className="text-success">none</span>
                    )}
                  </td>
                  <td className="td num text-right">{fine(s.variance_fine_wt)}</td>
                  <td className="td text-xs">{s.status.toLowerCase()}</td>
                </tr>
              ))}
            </Table>
          ) : null}
        </>
      ) : (
        <>
          <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat label="Expected on the shelf" value={String(variance.data?.expectedItems ?? '—')} />
            <Stat label="Counted so far" value={String(variance.data?.countedItems ?? 0)} tone="good" />
            <Stat
              label="Not found yet"
              value={String(variance.data?.missing.length ?? 0)}
              tone={variance.data && variance.data.missing.length > 0 ? 'warn' : undefined}
            />
            <Stat label="Not on the books" value={String(variance.data?.unexpected.length ?? 0)} />
          </div>

          <form autoComplete="off" onKeyDown={onEnter} onSubmit={scan} className="mb-4 flex gap-2">
            <input
              ref={scanRef}
              className="field max-w-sm"
              placeholder="Scan a tag"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              autoFocus
            />
            <button className="btn-ghost" disabled={count.isPending}>
              Count
            </button>
          </form>

          <div className="grid gap-6 lg:grid-cols-2">
            <section>
              <h2 className="mb-2 text-sm font-semibold">Just scanned</h2>
              {scanned.length === 0 ? (
                <Empty message="Nothing scanned yet." />
              ) : (
                <div className="max-h-80 overflow-y-auto rounded-lg border border-ink/10 bg-white">
                  {scanned.map((s, i) => (
                    <div
                      key={`${s.code}-${i}`}
                      className="flex items-center gap-3 border-b border-ink/10 px-3 py-1.5 text-sm"
                    >
                      <span className="num">{s.itemCode ?? s.code}</span>
                      <span className={`ml-auto text-xs ${OUTCOME_TONE[s.outcome] ?? ''}`}>
                        {s.outcome === 'UNEXPECTED' ? 'not on the books' : s.outcome.replace(/_/g, ' ').toLowerCase()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section>
              <h2 className="mb-2 text-sm font-semibold">Still not found</h2>
              {variance.data && variance.data.missing.length > 0 ? (
                <div className="max-h-80 overflow-y-auto rounded-lg border border-ink/10 bg-white">
                  {variance.data.missing.map((m) => (
                    <div
                      key={m.itemCode}
                      className="flex items-center gap-3 border-b border-ink/10 px-3 py-1.5 text-sm"
                    >
                      <span className="num">{m.itemCode}</span>
                      <span className="num ml-auto text-xs text-ink-soft">{fine(m.fineWt)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <Empty message="Everything expected has been found." />
              )}
            </section>
          </div>

          <div className="mt-6 flex flex-wrap items-end gap-3">
            <div className="flex-1">
              <Field label="Notes on the count" hint="Kept with the session, and copied onto any adjustment raised.">
                <TextInput value={notes} onChange={setNotes} placeholder="Quarterly count, front showroom" />
              </Field>
            </div>
            <button
              className="btn-primary"
              disabled={close.isPending || notes.trim().length < 3}
              onClick={() => close.mutate()}
            >
              {close.isPending ? 'Closing…' : 'Close the count'}
            </button>
            <button className="btn-ghost" onClick={() => setActive(null)}>
              Leave it open
            </button>
          </div>

          <p className="mt-3 max-w-2xl text-xs text-ink-soft">
            Closing raises an adjustment for whatever was not found. It does not write anything off — the pieces stay on
            the books until a second person approves it.
          </p>
        </>
      )}
    </>
  );
}
